import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatDateOnly } from 'utils';

/**
 * Collapsible heading band used to label the CY and PY blocks of an
 * ageing-matrix Note (Trade Payables today; Trade Receivables, ST loans
 * ageing, and any future Note with
 * `meta_data.display_options.py_layout = "separate_grid"`).
 *
 * Renders:
 *   ▸ Ageing of {noteName} as at {DD MMM YYYY} is as under:
 *
 * Click the band header to collapse / expand the body (the matrix table
 * itself is rendered as `children`). CY defaults to expanded; PY to
 * collapsed — pattern mirrors `DisclosuresPane.tsx` where the first
 * disclosure is open by default and the rest start collapsed.
 *
 * Date sourcing: the parent (`MainNotePane`) hydrates `periodEndDate` /
 * `priorPeriodEndDate` from the backend (FsaEngagementSettings.period_end
 * with financial_year_end fallback). Both ISO yyyy-MM-dd; this component
 * formats to PRIMA's DD MMM YYYY convention via `formatDateOnly`.
 *
 * Reusable: the same component wraps the CY matrix and the PY matrix —
 * caller picks the right date + default-open state.
 */

interface AgeingSectionBandProps {
  noteName: string;
  /** ISO yyyy-MM-dd. Falls back to `'—'` if absent. */
  periodEndDateIso?: string | null;
  /** Default open state — true for CY, false for PY. */
  defaultOpen?: boolean;
  /**
   * Optional custom band title. When provided it replaces the default
   * "Ageing of {noteName} as at {date} is as under:" wording — used by
   * non-ageing section-grid Notes (e.g. the Owners' Capital Account Note's
   * "(a) Partners' Capital Account" / "(b) Partners' Current Account"
   * sections) that reuse this same collapsible-panel pattern.
   */
  title?: React.ReactNode;
  children: React.ReactNode;
}

const AgeingSectionBand: React.FC<AgeingSectionBandProps> = ({
  noteName,
  periodEndDateIso,
  defaultOpen = true,
  title,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const dateLabel = formatDateOnly(periodEndDateIso ?? null, '—');

  return (
    <div style={containerStyle}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={headerStyle}
      >
        <IconSystem.chevronRight
          size={14}
          style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
        />
        <span style={titleStyle}>
          {title != null
            ? title
            : <>Ageing of {noteName} as at <strong>{dateLabel}</strong> is as under:</>}
        </span>
      </button>
      {open && <div style={bodyStyle}>{children}</div>}
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  marginBottom: 12,
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 6,
  background: 'var(--bg-primary, #fff)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  background: 'var(--bg-secondary, #f8fafc)',
  border: 'none',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: '6px 6px 0 0',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'var(--text-primary, #1e293b)',
  fontFamily: 'inherit',
};

const titleStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
};

const bodyStyle: React.CSSProperties = {
  padding: 0,
};

export default AgeingSectionBand;
